'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import type { ExtendedListing } from '@/src/lib/api/endpoints/listing';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { useOptionalListingActionContext } from '@/src/components/features/listing-actions';
import { useCurrentUser } from '@/src/lib/hooks/query';
import { analytics } from '@/src/components/features/property/lib';
import { isWaitlistedListing } from '@/src/components/features/property/lib/waitlist';
import { toHotpadsClickstreamListing } from '@/src/analytics/hotpads/track';
import type { ContactSubmissionContext } from './types';

const ContactFormOverlay = dynamic(() => import('./ContactFormOverlay').then((mod) => mod.ContactFormOverlay));

const PostContactFlowModal = dynamic(() => import('./PostContactFlowModal').then((mod) => mod.PostContactFlowModal));

/**
 * Compatibility hook for existing CTA call sites. The actual Contact modal is
 * hosted by ListingActionProvider so nested parent modals stay mounted while
 * contact/post-contact flows appear above them.
 */
export const useContactFormAction = (
  listing?: PropertyDetailListing | ExtendedListing | null,
  triggerLocation = 'home_details_halo',
  triggerObject = 'home_details_component|modal_contact_form',
) => {
  const t = useTranslations('contactForm');
  const { data: currentUser } = useCurrentUser();
  const identityResolved = currentUser !== undefined;
  const listingActionContext = useOptionalListingActionContext();
  const [isOpen, setIsOpen] = useState(false);
  const [postContactContext, setPostContactContext] = useState<ContactSubmissionContext | null>(null);

  const openContactForm = useCallback(() => {
    if (!identityResolved) return;

    const title = isWaitlistedListing(listing) ? t('titleWaitlist') : t('titleContact');

    if (listingActionContext) {
      listingActionContext.openContact(listing, {
        triggerLocation,
        triggerObject,
        title,
      });
      return;
    }

    analytics.emitTrackContactClickEvent({
      triggerLocation,
      triggerObject,
      listing: toHotpadsClickstreamListing(listing),
      phase: 'open',
    });
    setIsOpen(true);
  }, [identityResolved, listing, listingActionContext, t, triggerLocation, triggerObject]);

  const handleSubmitSuccess = useCallback((context: ContactSubmissionContext) => {
    setIsOpen(false);
    setPostContactContext(context);
  }, []);

  if (listingActionContext) {
    return {
      openContactForm,
      contactFormOverlay: null,
      identityResolved,
    };
  }

  const contactFormOverlay =
    isOpen || postContactContext ? (
      <>
        {isOpen && (
          <ContactFormOverlay
            open={isOpen}
            onOpenChange={setIsOpen}
            title={isWaitlistedListing(listing) ? t('titleWaitlist') : t('titleContact')}
            listing={listing}
            mode="contact"
            onSubmitSuccess={handleSubmitSuccess}
          />
        )}
        <PostContactFlowModal context={postContactContext} onDone={() => setPostContactContext(null)} />
      </>
    ) : null;

  return {
    openContactForm,
    contactFormOverlay,
    identityResolved,
  };
};
